/**
 * Any new key added in the prescription, might needs to be taken care at following places as well
 * - @see BorrowPrescription.tsx
 * - @see FetchUpsertPrescription.tsx
 * - @see PastVisitRightPanel.tsx
 * - @see idbBackUpReduxMiddleware.ts
 * - @see APPLY_DRAFT px action
 * - @see pushVisit.ts
 * - @see CLEAR_PRESCRIPTION px action
 * - @see CREATE_PRESCRIPTION_TEMPLATE px action
 * - @see isPPFilled func
 */
import { CreateReferalData } from '../../api/createReferal';
import { VISIT_TYPE } from '../../api/fetchAllVisitsForPatient';
import { HXNG_MAPPED, HXNG_STATUS } from '../../api/fetchDrugsAndLabTestsStatus';
import { AllUnitsEntity } from '../../api/fetchMatchingLabVitals';
import { Relation } from '../../api/fetchPatientDirByDocOid';
import { DocNetworkEntity } from '../../api/fetchSpecialityForDoctor';
import { PadDForm } from '../../api/pushVisit';
import { IMilestoneHistory } from '../../components/common/types/milestones';
import { PxElSourceTrack } from '../../components/prescriptionPad/trackSource';
import { CareCanvas } from './CareCanvas';
import { IVaccineStatus, MHC } from './medicalHistory';

export const toMultiRerferObj = (refer?: CreateReferalData): CreateReferalData | undefined => {
    if (!refer || refer.doc.length > 1 || !refer.doc.length) {
        // already multi refer || invalid refer
        return refer;
    }

    return {
        ...refer,
        doc: [
            {
                id: refer.referred_doctor_oid,
                referred_doctor_url: refer.referred_doctor_url,
                note: refer.note,
                s_name: refer.s_name,
                s_id: refer.id?.toString(),

                ...refer.doc[0],
            },
        ],
    };
};

export interface LabRefer {
    s_name: string;
    name: string;
    id?: number;
    group_id?: string;
    patientid: string;
    note?: string;
    eka_note?: string;
    refer_to_eka: boolean;
    campaign: boolean;
    created_at: string;

    lab: DocNetworkEntity[];
}

export interface ToolWithMH extends Tool {
    medicalHistory: MHC;
    sectionName?: string;
}

export interface OpVision {
    eye?: {
        value?: string;
    };
    ucdva?: {
        id: string;
        value?: string;
        custom?: string;
    };
    ucnva?: {
        id: string;
        value?: string;
        custom?: string;
    };
    ph?: {
        id: string;
        value?: string;
        custom?: string;
    };
    bcdva?: {
        id: string;
        value?: string;
        custom?: string;
    };
    bcnva?: {
        id: string;
        value?: string;
        custom?: string;
    };
}

export interface OpSpec {
    eye?: {
        value?: string;
    };
    sph?: {
        id: string;
        value?: string;
        custom?: string;
    };
    cyl?: {
        id: string;
        value?: string;
        custom?: string;
    };
    axis?: {
        id: string;
        value?: string;
        custom?: string;
    };
    add?: {
        id: string;
        value?: string;
        custom?: string;
    };
    dva?: {
        id: string;
        value?: string;
        custom?: string;
    };
    nva?: {
        id: string;
        value?: string;
        custom?: string;
    };
}

export interface OpIop {
    eye?: {
        value?: string;
    };
    nct?: {
        id: string;
        value?: string;
        custom?: string;
    };
    gat?: {
        id: string;
        value?: string;
        custom?: string;
    };
    cct?: {
        id: string;
        value?: string;
        custom?: string;
    };
    ciop?: {
        id: string;
        value?: string;
        custom?: string;
    };
}

export interface OpVisionElement extends OpVision {
    id: string;
}

export interface OpSpecElement extends OpSpec {
    id: string;
}

export interface OpIopElement extends OpIop {
    id: string;
}

export type PX_OP_KEYS =
    | 'opVision'
    | 'opIop'
    | 'opCurrentSpec'
    | 'opSubjectiveRefraction'
    | 'opAutoRefraction'
    | 'opFinalPrescription';

export interface ToolElements {
    examinations?: ExaminationEntity[];
    diagnosis?: DiagnosisEntityOrSymptomsEntity[];
    symptoms?: DiagnosisEntityOrSymptomsEntity[];
    vitals?: VitalsEntity[];
    medications?: MedicationsEntity[];
    injections?: InjectionsEntity[];
    labTests?: LabTestsEntityOrProceduresEntity[];
    advices?: AdvicesEntity[];
    dyform?: PadDForm[];
    labVitals?: LabVitalEntity[];
    dentalExaminations?: DentalExamination[];
    dentalProcedures?: DentalProcedure[];
    administeredVaccines?: IVaccineStatus[];
    allVaccines?: IVaccineStatus[];
    milestones?: IMilestoneHistory[];
    allMilestones?: IMilestoneHistory[];
    opVision?: OpVisionElement[];
    opIop?: OpIopElement[];
    opCurrentSpec?: OpSpecElement[];
    opSubjectiveRefraction?: OpSpecElement[];
    opAutoRefraction?: OpSpecElement[];
    opFinalPrescription?: OpSpecElement[];
    procedures?: Procedure[];
}

export type ToolElement = OpVisionElement | OpIopElement | OpSpecElement | Procedure;

export interface Tool extends ToolElements {
    followup?: Followup;
    prescriptionNotes?: Notes;
    privateNotes?: Notes;
    refer?: CreateReferalData;
    labRefer?: LabRefer;
    attachment?: Attachment;
    careCanvas?: CareCanvas[];
}

export type PadElements = Partial<ToolWithMH>;

export interface Kind {
    diagnosis?: DiagnosisEntityOrSymptomsEntity[];
    symptoms?: DiagnosisEntityOrSymptomsEntity[];
    medications?: MedicationsEntity[];
    injections?: InjectionsEntity[];
    labTests?: LabTestsEntityOrProceduresEntity[];
    labVitals?: LabVitalEntity[];
    examinations?: ExaminationEntity[];
    procedures?: Procedure[];
}

export interface Prescription extends Tool {
    id: string;
    date: Date;
    doctor: Doctor;
    patient: Patient;
    visitId: string;
    clinic: Clinic;

    patientHistory?: MHC; // medical history
    isPatientHistoryFetchFailed?: boolean;

    meta?: {
        templateId?: string;
        draftId?: string;
        autoCopyStatus?: 'REQUIRED' | 'COPIED' | 'NOT_REQUIRED';
    };

    _is_failed?: boolean;
    type?: VISIT_TYPE;
    doc_name?: string;
    ipdAdmission?: IpdAdmission;
}

export interface IpdAdmission {
    advised?: boolean;
    include_procedures?: boolean;
    notes?: string;
}

export interface LabVitalEntity {
    id: string;
    name: string;
    unit_dislay_name?: string;
    value?: string;
    interpretation?: Interpretation;
    date?: string;
    remark?: string;
    unit?: Unit;
    all_units?: AllUnitsEntity[] | null;
    is_panel?: boolean;
}

export interface Interpretation {
    value: string;
    id: string;
    eka_id?: string;
    name_list?: string[];
}
export interface Unit {
    name: string;
    id: string;
    refRange?: RefRange;
}
export interface RefRange {
    low?: string | null;
    high?: string | null;
}

export interface Notes {
    id: string;
    text: string;
    parsedText: string;
}

export interface ExaminationEntity {
    id: string;
    name: string;
    notes?: string;
}

export interface Doctor {
    _id: string;
    profile: DoctorProfile;
}

export interface DoctorProfile {
    personal: DoctorPersonalProfile;
    professional: DoctorProfessionalProfile;
}

export interface DoctorPersonalProfile {
    name: { f: string; l: string };
    phone?: { code: string; value: string };
    pic?: string;
}

export interface Phone {
    c?: string;
    n?: string;
}

export interface DoctorProfessionalProfile {
    registrations?: {
        council: string;
        id: string;
        medical_id: string;
        year: string;
    };
    degree: {
        id: string;
        name: string;
    }[];
    major_speciality?: {
        id: string;
        name: string;
    };
    speciality?: {
        id: string;
        name: string;
    }[];
    signature_text?: string;
    header_text?: string;
    default_clinic: string;
    invite?: {
        invite_code?: string;
        invite_link?: string;
    };
    templates?: {
        print?: {
            templateId: string;
            displayName: string;
        }[];
        send?: {
            templateId: string;
            displayName: string;
        }[];
    };
    signature: string;
    username: string;
    clinics?: Clinic[];
    category?: string[];
    business?: {
        id: string;
        name?: string;
        logo?: string;
    };
}

export interface Clinic {
    _id: string;
    city_id: string;
    city_name?: string;
    name: string;
    email?: string;
    number?: NumberEntity[] | null;
    address?: Address;
    price: Price;
    location?: string[] | null;
    logo: string;
    gplacesid?: string;
    abdm?: {
        hfr_id?: string;
    };
    partner_clinic_id?: string;
}
export interface NumberEntity {
    c: string;
    n: string;
}
export interface Address {
    line1: string;
    city: string;
    state: string;
    country: string;
    pin: string;
}
export interface Price {
    c: string;
    v: number;
}

export interface PatientWithLocalId {
    id?: string;
    localId: string;
    uuid?: string;
    profile: PatientProfile;
    formData?: {
        label: string;
        type: 'number' | 'string' | 'select';
        key: string;
        value: string | number;
    }[];
    'health-ids'?: string[];
    abha?: {
        id?: string;
        records?: number;
    };
    relations?: Relation[];
}

export interface Attachment {
    title: string;
    description: string;
    link: string;
    file_type: string;
    templateId?: string;
}

export type Patient = PatientWithLocalId;

export interface PatientProfile {
    personal: PatientPersonalProfile;
}

export interface PatientPersonalProfile {
    name: string;
    phone?: Phone;
    age?: Age;
    gender: string;
    onApp?: boolean;
    bloodgroup?: string;
}

export interface Age {
    dob?: string;
    value?: string;
    month?: string;
}

export interface SymDiaProperty {
    name: string;
    selection?: SelectionEntity[];
}
export interface SelectionEntity {
    value: string;
    unit?: string;
    id: string;
}

export interface DiagnosisEntityOrSymptomsEntity {
    name: string;
    id: string;
    duration?: Duration;
    severity?: Severity;
    status?: SymDiaStatus; // only is diagnosis // TODO create new type
    remark?: string;
    isLocal?: boolean; // If created local

    // server properties
    properties?: { [k: string]: SymDiaProperty };
    icd10_code?: string;
    icd10_name?: string;
}
export interface Duration {
    value?: string;
    unit?: string;
    custom?: string;
}
export interface Severity {
    code: string;
    display: string;
}
export interface SymDiaStatus {
    code: string;
    display: string;
}
export interface VitalsEntity {
    name: string;
    dis_name: string;
    id: string;
    value: Value;
    interpretation?: string;
    /**
     * @deprecated Use isoDate
     */
    date?: string;
    isoDate?: string;
    remark?: string;
    locked?: boolean;
}
export type CalculatorEntity = VitalsEntity;
export interface Value {
    qt: string;
    unit: string; // same as be dis_unit
    code?: string; // unit code, refer mdb/v1/vitals api for more details
    code_id?: string; // unit code id, refer mdb/v1/vitals api for more details
    safe?: Safe;
}

export interface Safe {
    low?: string;
    high?: string;
    type: 'Numeric' | 'string' | string;
    is_decimal_allowed?: boolean;
    normal_value?: string;
}

export interface Frequency {
    type: string; // either code or onceEvery or SOS or toContinue or custom
    code?: string;
    onceEvery?: {
        value: string;
        unit: string;
    };
    custom?: string;
    time_split?: MedDose[];
}

export interface MedDose {
    id?: string;
    value?: string;
    unit?: string;
    custom?: string;
    timing?: Date | string;
}

export interface MedDoseInfo {
    dose?: MedDose;
    area?: { name: string; id: string };
    frequency?: Frequency;
    timing?: string;
    duration?: Duration;
    quantity?: {
        custom?: string;
    };
    instruction?: string;
    start_from?: {
        text: string;
        value: string;
        unit: string;
    };
    to_day?: {
        text: string;
        value: string;
        unit: string;
    };
    route?: MedRoute;
}

export type MedicineCategory = 'advice' | 'device' | 'nad';

// Anything updated here, also needs attension in MedTableV2 rendering
export interface MedicationsEntity
    extends MedDoseInfo,
        // only for tracking purposes
        PxElSourceTrack {
    name: string;
    id: string;

    // returned by MDB
    // not editable, should be shown on label in cards, near delete icon
    // added laterly, fallback to form
    dosage_form?: string;

    // this is the form input prompt to user, when df_id is not found
    // associated keys => dose.custom, basically !dose.id
    // not related to mdb
    form?: string;

    product_type?: string;
    generic_name?: string;
    generic_id?: string;
    df_id?: string;

    tapering_dose?: MedDoseInfo[];
    soa?: boolean;

    // contains the original name from mdb, before renaming
    actual_name?: string;

    is_otc?: boolean;
    manufacturer_name?: string;
    manufacturer_id?: string;
    med_cat?: MedicineCategory;
    hxng_mapped?: HXNG_MAPPED;
    hxng_status?: HXNG_STATUS;
    inventory_status?: {
        in_stock: boolean;
        price: number;
        quantity: number;
        evital_id: string;
    };
    meta_info?: {
        dose?: QuantityWithUnit;
        mass?: QuantityWithUnit;
        volume?: QuantityWithUnit;
    };
    route?: MedRoute;
}

export interface MedRoute {
    name: string;
    id: string;
    display_name: string;
    snomed_id: string;
}

export interface QuantityWithUnit {
    value?: number;
    unit?: string;
}

export interface InjectionsEntity {
    name: string;
    id: string;
    generic_name: string;
    form: string;
    df_id?: string;
    dosage_form?: string;
    route: string;
    dose: MedDose;
    infusion_rate: InfusionRate;
    frequency: Frequency;
    duration: Duration;
    timing: string;
    instructions?: string;
    tapering_dose?: InjectionsEntity[];
    added_drug?: InjectionsEntity[];
    actual_name?: string;
}

export type FlatInjectionObj = InjectionsEntity & {
    mainIndex: number;
    currentIndex: number;
    is_last_tapering?: boolean;
    is_last_drug?: boolean;
    is_added_drug?: boolean;
    is_tapering_dose?: boolean;
};

export type TInjectionAddedDrug = Pick<
    InjectionsEntity,
    'id' | 'name' | 'form' | 'generic_name' | 'dose'
>;

export interface InfusionRate {
    total_volume?: MedDose;
    infusion_time?: MedDose;
    iv_drop_rate?: MedDose;
    iv_rate?: MedDose;
}
export interface LabTestsEntityOrProceduresEntity {
    name: string;
    id: string;
    remark?: string;
    book?: boolean;
    test_on?: string;
    repeat_on?: string;
    type?: string;
    content?: string[];
    vendor_names?: string[];
    vendor_ids?: string[];
    hxng_only?: boolean;
}
export interface Followup {
    date?: string;
    appointmentId?: string;
    optionId?: string; // for analytic purpose
    notes?: string;
}
export interface AdvicesEntity {
    id: string;
    text: string;
    parsedText: string;
}

export enum DENTAL_ENTITY_TYPE {
    EXAMINATION = 'examination',
    PROCEDURE = 'procedure',
}

export interface DentalExamination {
    entity_id?: string;
    id: string;
    name: string;
    teeth_id?: string;
    surfaces?: Surface[];
    since?: string;
    remark?: string;
    group_id?: string;
    type?: DENTAL_ENTITY_TYPE; // examination or procedure
    index?: number;
}

export interface GMedicalRecordsTags {
    entity_id?: string;
    id: string;
    name: string;
}

export interface DoctorCalendarConfigs {
    slotsFromDoctorCalendar: boolean;
}

export type TDoctorEvitalsConfigs = {
    manualPushOrder: boolean;
};

export interface DentalProcedure {
    entity_id?: string;
    id: string;
    name: string;
    teeth_ids?: string[];
    surfaces?: Surface[];
    visits?: string;
    start_on?: string;
    remark?: string;
    service_id?: string;
    conducted_by?: string;
    assisted_by?: string;
    index?: number;
}

export interface Surface {
    id: string;
    name: string;
}

export interface Procedure {
    id: string;
    name: string;
    date?: string;
    notes?: string;
}

export enum CopyType {
    MX = 'MX',
    CurrentMX = 'CurrentMX',
    INJ = 'INJ',
}
